"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatArabicDate, truncateText } from "@/app/funcs/funcs";
import { usePodcast } from "@/app/context/PodcastContext";
import ViewToggle, { getStoredViewMode, setStoredViewMode } from "../components/ViewToggle";

const PAGE_SIZE = 9;

function stripHtml(html = "") {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export default function PodcastsPage() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const { playEpisode, currentEpisode, isPlaying } = usePodcast();

  useEffect(() => {
    setViewMode(getStoredViewMode());
  }, []);

  const handleViewChange = (mode) => {
    setViewMode(mode);
    setStoredViewMode(mode);
  };

  const fetchEpisodes = async (page, append = false) => {
    try {
      setLoading(true);
      if (!append) setError(null);

      const res = await fetch(`/api/podcasts/rss?page=${page}&per_page=${PAGE_SIZE}`);
      if (!res.ok) throw new Error(`RSS API returned ${res.status}`);

      const json = await res.json();
      const data = Array.isArray(json?.data) ? json.data : [];

      setEpisodes((prev) => (append ? [...prev, ...data] : data));
      setCurrentPage(Number(json?.currentPage || page));
      setHasMore(Number(json?.currentPage || page) < Number(json?.totalPages || 1));
    } catch (err) {
      console.error("Error fetching podcast RSS:", err);
      if (!append) {
        setError(err?.message || "حدث خطأ في تحميل حلقات البودكاست");
        setEpisodes([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setEpisodes([]);
    setCurrentPage(1);
    setHasMore(true);
    fetchEpisodes(1, false);
  }, []);

  useEffect(() => {
    if (!hasMore || loading || !sentinelRef.current) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          fetchEpisodes(currentPage + 1, true);
        }
      },
      { rootMargin: "250px" }
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [currentPage, hasMore, loading]);

  const filteredEpisodes = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return episodes;
    return episodes.filter((ep) => {
      const title = stripHtml(ep?.title?.rendered || "").toLowerCase();
      const excerpt = stripHtml(ep?.excerpt?.rendered || "").toLowerCase();
      const author = String(ep?.author || "").toLowerCase();
      return title.includes(q) || excerpt.includes(q) || author.includes(q);
    });
  }, [episodes, searchTerm]);

  
  const [expandedGroups, setExpandedGroups] = useState({});

  const groupedEpisodes = useMemo(() => {
    const groups = {};
    filteredEpisodes.forEach((ep) => {
      const season = ep.season ? ep.season.toString() : "other";
      if (!groups[season]) groups[season] = [];
      groups[season].push(ep);
    });

    const sortedSeasons = Object.keys(groups).sort((a, b) => {
      if (a === "other") return 1;
      if (b === "other") return -1;
      return Number(b) - Number(a);
    });

    return sortedSeasons.map((season) => ({
      season,
      episodes: groups[season],
    }));
  }, [filteredEpisodes]);

  useEffect(() => {
    if (groupedEpisodes.length > 0 && Object.keys(expandedGroups).length === 0) {
      setExpandedGroups({ [groupedEpisodes[0].season]: true });
    }
  }, [groupedEpisodes.length]);

  const toggleGroup = (season) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [season]: !prev[season],
    }));
  };

  const isInitialLoading = loading && episodes.length === 0;

  return (
    <main className="container py-5" dir="rtl">
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold mb-3">البودكاست</h1>
        <p className="lead text-muted mx-auto" style={{ maxWidth: "700px" }}>
          استمع إلى حلقات بودكاست ملهمة وتعلم من خبرات وتجارب متنوعة في التصميم.
        </p>
      </div>

      <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
        <ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
        <input
          type="text"
          placeholder="ابحث عن حلقة..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-control rounded-pill"
          style={{ maxWidth: "250px" }}
        />
      </div>

      {isInitialLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
          <p className="mt-3 text-muted">جاري تحميل حلقات البودكاست...</p>
        </div>
      )}

      {error && !isInitialLoading && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button
            className="btn btn-sm btn-outline-warning ms-3"
            onClick={() => fetchEpisodes(1, false)}
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {!isInitialLoading && (
        <>
          {groupedEpisodes.length > 0 ? (
            <div className="d-flex flex-column" style={{ gap: "3rem" }}>
              {groupedEpisodes.map((group) => {
                const isExpanded = expandedGroups[group.season];
                const groupTitle = group.season === "other" ? "حلقات أخرى" : `الموسم ${group.season}`;

                return (
                  <div key={group.season} className="episode-group">
                    <div 
                      className="position-relative mb-4" 
                      onClick={() => toggleGroup(group.season)}
                      style={{ cursor: "pointer" }}
                    >
                      {!isExpanded && (
                        <>
                          <div className="card shadow-sm border-0 rounded-4 bg-white position-absolute w-100" style={{ top: "8px", right: "-8px", zIndex: 0, opacity: 0.5, height: "100%" }}></div>
                          <div className="card shadow-sm border-0 rounded-4 bg-white position-absolute w-100" style={{ top: "4px", right: "-4px", zIndex: 1, opacity: 0.8, height: "100%" }}></div>
                        </>
                      )}
                      <div className="card shadow-sm border-0 rounded-4 bg-white position-relative hover-shadow" style={{ zIndex: 2, transition: "all 0.3s ease" }}>
                        <div className="card-body d-flex align-items-center justify-content-between p-4">
                          <div className="d-flex align-items-center gap-3">
                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: "55px", height: "55px" }}>
                              <i className={`bi ${group.season === "other" ? "bi-mic" : "bi-collection"} fs-4`}></i>
                            </div>
                            <div>
                              <h2 className="h4 fw-bold mb-1">{groupTitle}</h2>
                              <span className="text-muted small">{group.episodes.length} حلقة</span>
                            </div>
                          </div>
                          <div className="bg-light rounded-circle flex-center d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                            <i className={`bi bi-chevron-${isExpanded ? "up" : "down"} fs-5 text-dark`} style={{ transition: "transform 0.3s" }}></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="animate__animated animate__fadeIn">
            {viewMode === "grid" ? (
              <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                {group.episodes.map((item) => {
                  const isCurrentPlaying = currentEpisode?.id === item.id && isPlaying;
                  const episodeHref = `/sections/podcasts/${encodeURIComponent(item.id)}`;

                  return (
                    <div key={item.id} className="col">
                      <article className="card h-100 shadow-sm border-0 rounded-4 bg-white">
                        <Link href={episodeHref} className="text-decoration-none text-dark d-block">
                          {item.featured_image_urls?.medium ? (
                            <img
                              src={item.featured_image_urls.medium}
                              alt={stripHtml(item?.title?.rendered || "حلقة")}
                              className="card-img-top"
                              style={{ objectFit: "cover", width: "100%", maxHeight: "220px" }}
                            />
                          ) : (
                            <div className="card-img-top d-flex align-items-center justify-content-center bg-dark text-white" style={{ height: "220px" }}>
                              <i className="bi bi-mic" style={{ fontSize: "3rem" }}></i>
                            </div>
                          )}

                          <div className="card-body d-flex flex-column">
                            <h2 className="h5 fw-bold mb-2" dangerouslySetInnerHTML={{ __html: item?.title?.rendered || "بدون عنوان" }}></h2>
                            <span className="d-block mb-2 text-muted">
                              <i className="bi bi-calendar me-1"></i>
                              {formatArabicDate(item.date)}
                              {item.duration && <span className="ms-3"><i className="bi bi-clock me-1"></i>{item.duration}</span>}
                              {item.season && <span className="ms-3"><i className="bi bi-collection me-1"></i>موسم {item.season}</span>}
                              {item.episodeNum && <span className="ms-3"><i className="bi bi-hash me-1"></i>حلقة {item.episodeNum}</span>}
                            </span>
                            <p className="text-muted mb-2 flex-grow-1">
                              {truncateText(stripHtml(item?.excerpt?.rendered || ""), 150)}
                            </p>
                            {item.author && (
                              <div className="d-flex align-items-center rounded-pill py-1 px-2 border bg-white w-fit">
                                <i className="bi bi-mic me-2 text-muted"></i>
                                <small className="text-dark fw-bold">{item.author}</small>
                              </div>
                            )}
                          </div>
                        </Link>

                        {item.audioUrl && (
                          <div className="card-footer border-0 bg-transparent pt-0" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={`btn w-100 d-flex align-items-center justify-content-center gap-2 p-2 rounded-pill ${isCurrentPlaying ? "btn-primary" : "btn-outline-dark"}`}
                              onClick={() => playEpisode(item)}
                            >
                              {isCurrentPlaying ? (
                                <><i className="bi bi-pause-circle fs-5"></i><span>جاري الاستماع...</span><div className="spinner-grow spinner-grow-sm text-white ms-2" role="status"><span className="visually-hidden">Loading...</span></div></>
                              ) : (
                                <><i className="bi bi-play-circle fs-5 flip-hh"></i><span>استمع للحلقة</span></>
                              )}
                            </button>
                          </div>
                        )}
                      </article>
                    </div>
                  );
                })}
              </div>
            ) : (
              <ul className="list-unstyled d-flex flex-column gap-3">
                {group.episodes.map((item) => {
                  const isCurrentPlaying = currentEpisode?.id === item.id && isPlaying;
                  const episodeHref = `/sections/podcasts/${encodeURIComponent(item.id)}`;

                  return (
                    <li key={item.id}>
                      <article className="card shadow-sm border-0 rounded-4 bg-white overflow-hidden">
                        <div className="row g-0">
                          <div className="col-12 col-md-4 col-lg-3">
                            {item.featured_image_urls?.medium ? (
                              <img src={item.featured_image_urls.medium} alt={stripHtml(item?.title?.rendered || "حلقة")} className="img-fluid w-100 h-100" style={{ objectFit: "cover", minHeight: "160px", maxHeight: "200px" }} />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center bg-dark text-white w-100" style={{ minHeight: "160px" }}>
                                <i className="bi bi-mic" style={{ fontSize: "2.5rem" }}></i>
                              </div>
                            )}
                          </div>
                          <div className="col-12 col-md-8 col-lg-9">
                            <div className="card-body d-flex flex-column justify-content-center">
                              <Link href={episodeHref} className="text-decoration-none text-dark">
                                <h2 className="h5 fw-bold mb-2" dangerouslySetInnerHTML={{ __html: item?.title?.rendered || "بدون عنوان" }}></h2>
                              </Link>
                              <span className="d-block mb-2 text-muted">
                                <i className="bi bi-calendar me-1"></i>
                                {formatArabicDate(item.date)}
                                {item.duration && <span className="ms-3"><i className="bi bi-clock me-1"></i>{item.duration}</span>}
                                {item.season && <span className="ms-3"><i className="bi bi-collection me-1"></i>موسم {item.season}</span>}
                                {item.episodeNum && <span className="ms-3"><i className="bi bi-hash me-1"></i>حلقة {item.episodeNum}</span>}
                              </span>
                              <p className="text-muted mb-2">
                                {truncateText(stripHtml(item?.excerpt?.rendered || ""), 200)}
                              </p>
                              {item.author && (
                                <div className="d-flex align-items-center rounded-pill py-1 px-2 border bg-white w-fit mb-2">
                                  <i className="bi bi-mic me-2 text-muted"></i>
                                  <small className="text-dark fw-bold">{item.author}</small>
                                </div>
                              )}
                              {item.audioUrl && (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className={`btn btn-sm d-flex align-items-center gap-2 rounded-pill ${isCurrentPlaying ? "btn-primary" : "btn-outline-dark"}`}
                                    onClick={() => playEpisode(item)}
                                  >
                                    {isCurrentPlaying ? <><i className="bi bi-pause-circle"></i><span>جاري الاستماع...</span></> : <><i className="bi bi-play-circle flip-hh"></i><span>استمع</span></>}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    </li>
                  );
                })}
              </ul>
            )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">لا يوجد حلقات بودكاست متاحة حالياً.</p>
            </div>
          )}

          {hasMore && (
            <div ref={sentinelRef} className="py-4 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">جاري التحميل...</span>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}
